import React from 'react';
import styled from 'styled-components';

const HeroSection = () => {
  return (
    <HeroContainer>
      <LogoContainer>
        <Logo>Axon</Logo>
      </LogoContainer>
      <ContentWrapper>
        <TextContainer>
          <EyebrowText>Impact Millions.</EyebrowText>
          <Headline>
            Your Coaching.<br />
            <AccentText>Unlimited Reach.</AccentText>
          </Headline>
          <Subheading>
          Easily deliver hyper-personalized coaching to all of your followers, while preserving your unique brand.
          </Subheading>
          <ButtonGroup>
            <CTAButton href="https://airtable.com/applMg99eyh4tG3sV/pag7Q14LFPQXgJgTi/form">
              <ButtonText>Join beta</ButtonText>
              <ButtonArrow>→</ButtonArrow>
            </CTAButton>
          </ButtonGroup>
          <SocialProof>
            <SocialProofText>Already helping coaches reach</SocialProofText>
            <StatHighlights>
              <Stat>
                <StatNumber>100x</StatNumber>
                <StatLabel>more clients</StatLabel>
              </Stat>
              <StatDivider />
              <Stat>
                <StatNumber>98%</StatNumber>
                <StatLabel>client satisfaction</StatLabel>
              </Stat>
              <StatDivider />
              <Stat>
                <StatNumber>3hrs</StatNumber>
                <StatLabel>saved daily</StatLabel>
              </Stat>
            </StatHighlights>
          </SocialProof>
        </TextContainer>
        
        <MockupContainer>
          <PhoneMockup>
            <PhoneFrame />
            <PhoneImage src="/images/app-screenshot.png" alt="Axon AI coaching platform for health and wellness influencers to scale their business and train unlimited clients" />
            <FeatureHighlight top="35%" left="30%" direction="left">
              <FeatureLabel parent={{ direction: 'left' }}>AI nutrition tracking</FeatureLabel>
            </FeatureHighlight>
            <FeatureHighlight top="60%" left="20%">
              <FeatureLabel parent={{ direction: 'row' }}>AI workout plans</FeatureLabel>
            </FeatureHighlight>
            <FeatureHighlight top="81%" left="40%" direction="left">
              <FeatureLabel parent={{ direction: 'left' }}>AI messaging</FeatureLabel>
            </FeatureHighlight>
            <FeatureHighlight top="90%" left="20%">
              <FeatureLabel parent={{ direction: 'row' }}>Habit tracking, meal plans & more...</FeatureLabel>
            </FeatureHighlight>
          </PhoneMockup>
        </MockupContainer>
      </ContentWrapper>
    </HeroContainer>
  );
};

const HeroContainer = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem 6rem 5rem;
  position: relative;
  overflow: hidden;
  background: #FBFBFB;
  
  @media (max-width: 768px) {
    padding: 1.5rem 2rem;
  }
`;

const LogoContainer = styled.div`
  padding-bottom: 1rem;
  position: relative;
  z-index: 2;
`;

const Logo = styled.div`
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  font-weight: 300;
  font-size: 36px;
  color: #1D1D1F;
  letter-spacing: -0.03em;
  
  &::after {
    content: '.';
    color: #BA865E;
    font-weight: 400;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
  }
`;

const EyebrowText = styled.div`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 13px;
  font-weight: 500;
  color: #BA865E;
  margin-bottom: 1.5rem;
`;

const TextContainer = styled.div`
  max-width: 510px;
  z-index: 2;
  
  @media (max-width: 1024px) {
    margin-top: 2rem;
    margin-bottom: 3.5rem;
  }
`;

const Headline = styled.h1`
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  font-size: clamp(48px, 5vw, 68px);
  line-height: 1.07;
  font-weight: 300;
  color: #1D1D1F;
  margin-bottom: 1.8rem;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: clamp(40px, 7vw, 48px);
  }
`;

const AccentText = styled.span`
  color: #BA865E;
  position: relative;
  font-weight: 400;
`;

const Subheading = styled.p`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  line-height: 1.5;
  margin-bottom: 3rem;
  color: #86868b;
  font-weight: 400;
  max-width: 490px;
  
  @media (max-width: 768px) {
    font-size: 17px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CTAButton = styled.a`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  background: linear-gradient(135deg, #BA865E 0%, #C4845F 100%);
  color: white;
  font-size: 17px;
  padding: 0.9rem 2.2rem;
  border: none;
  border-radius: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(186, 134, 94, 0.25);
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(186, 134, 94, 0.35);
    background: linear-gradient(135deg, #C4845F 0%, #BA865E 100%);
    color: white;
  }
`;

const ButtonText = styled.span`
  position: relative;
  z-index: 2;
`;

const ButtonArrow = styled.span`
  margin-left: 0.8rem;
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
  
  ${CTAButton}:hover & {
    transform: translateX(4px);
  }
`;

const SocialProof = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const SocialProofText = styled.div`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  color: #86868b;
  font-weight: 400;
`;

const StatHighlights = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  margin-top: 0.5rem;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatNumber = styled.div`
  font-family: 'SF Pro Display', 'Satoshi', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: #BA865E;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  color: #86868b;
  font-weight: 400;
  margin-top: 6px;
`;

const StatDivider = styled.div`
  height: 40px;
  width: 1px;
  background-color: rgba(39, 39, 39, 0.12);
`;

const MockupContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 530px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const PhoneMockup = styled.div`
  width: 360px;
  position: relative;
  transform: perspective(1200px) rotateY(-8deg) rotateX(2deg);
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;
  
  &:hover {
    transform: perspective(1200px) rotateY(-5deg) rotateX(2deg);
  }
  
  @media (max-width: 1024px) {
    width: 340px;
    transform: none;
    
    &:hover {
      transform: none;
    }
  }
`;

const PhoneFrame = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  z-index: 0;
`;

const PhoneImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0;
  position: relative;
  z-index: 1;
  object-fit: cover;
  object-position: top;
  filter: brightness(1.05);
`;

const FeatureHighlight = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  z-index: 3;
  display: flex;
  align-items: center;
  animation: pulse 3s infinite ease-in-out;
  flex-direction: ${props => props.direction === 'left' ? 'row-reverse' : 'row'};
  
  &::before {
    content: '';
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #BA865E;
    box-shadow: 0 0 0 4px rgba(186, 134, 94, 0.2);
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
`;

const FeatureLabel = styled.div`
  font-family: 'SF Pro Text', 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1D1D1F;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 0.6rem 1rem;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  margin-left: ${props => props.parent?.direction === 'left' ? 0 : '12px'};
  margin-right: ${props => props.parent?.direction === 'left' ? '12px' : 0};
  white-space: nowrap;
  opacity: 0;
  transform: ${props => props.parent?.direction === 'left' ? 'translateX(10px)' : 'translateX(-10px)'};
  transition: all 0.3s ease;
  
  ${FeatureHighlight}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;

export default HeroSection;